import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown, faEraser, faCopy } from '@fortawesome/free-solid-svg-icons';
import figlet from "figlet";
import standard from "figlet/importable-fonts/Standard.js";
import { toPng  } from 'html-to-image';
import StyledSelect from '../components/StyledSelect'
import SyntaxHighlighter from 'react-syntax-highlighter';
const BE_API_URL = process.env.REACT_APP_BACKEND_API_URL;

async function loadFonts(listFonts) {
  const importedStyles = await Promise.all(
    listFonts.map((style) =>
      import(`figlet/importable-fonts/${style}.js`)
    )
  );
  const fontsMap = {};
  listFonts.forEach((style, index) => {
    fontsMap[style] = importedStyles[index].default;
  });
  return fontsMap;
}

const AsciiArtTextGenerator = (props) => {
  const { appTheme } = props;
  const codeRef = useRef();
  const [originalInput, setOriginalinput] = useState('TOOlover');
  const [textOutput, setTextOutput] = useState('');
  const [fonts, setFonts] = useState(null)
  const [fontOptions, setFontsOptions] = useState([])
  const [fontSelection, setFontSelection] = useState(null)

  const handleInputText = (event) => {
    setOriginalinput(event.target.value);
  };

  const generateAsciiArt = (text) => {
    const fontValue = fonts && fontSelection ? fonts[fontSelection.value] : standard
    figlet.parseFont("Standard", fontValue);
    figlet.text(
      text, {},
      function (err, data) {
        if (err) {
          console.log('Something went wrong...');
          console.dir(err);
          return;
        }
        console.log(data);
        setTextOutput(data);
      }
    );
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText(textOutput);
  };

  const downloadAsImage = async () => {
    if (codeRef.current === null) {
      return;
    }
    toPng(codeRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `ascii-art.png`
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Oops, something went wrong!', err);
      });
  };

  useEffect(() => {
    if (originalInput && fontSelection) {
      generateAsciiArt(originalInput);
    } else {
      setTextOutput('')
    }
    if (!fonts) {
      fetch(`${BE_API_URL}/api/tools/asciiFonts`, {
        method: 'GET'
      })
        .then(response => response.json())
        .then(data => {
          if (data.status) {
            const listFonts = data.data
            const fontOpts = listFonts.map((font) => {
              return { label: font, value: font }
            })
            setFontsOptions(fontOpts)
            setFontSelection(fontOpts.filter((font) => font.value === 'ANSI Regular')[0])
            loadFonts(listFonts).then((fontsMap) => {
              setFonts(fontsMap)
            })
          }
        })
        .catch(error => {
          console.error(error);
          return [];
        });
    }
  }, [originalInput, fonts, fontSelection]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 xl:gap-4">
      <div className="col-span-1">
        <div className="h-full p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-blue-600 p-2'>Text Input</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600 h-64">
            <textarea
              id="inputText"
              rows={10}
              className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Text Goes Here"
              value={originalInput}
              onChange={handleInputText}
            />
          </div>
          <div className="px-4 py-2 text-gray-400 border-dashed rounded dark:border-gray-600">
            <StyledSelect appTheme={appTheme} value={fontSelection} onChange={(option) => setFontSelection(option)} className='w-2/4 sm:w-full inline-block' options={fontOptions} />
            &nbsp;
            <button
              onClick={() => setOriginalinput('')}
              type="button"
              className="text-white sm:mt-2 sm:w-full bg-gray-400 hover:bg-gray-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-block items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faEraser}/>
              &nbsp;
            </button>
          </div>
        </div>
      </div>

      <div className="col-span-2">
        <div className="h-full p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-yellow-600 p-2'>ASCII Art Preview</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600 h-64">
            <div ref={codeRef}>
              <SyntaxHighlighter
                customStyle={{
                  borderRadius: 8,
                  borderWidth: 1 ,
                  padding: 10,
                  overflow: 'auto',
                  height: '30vh',
                  backgroundColor: (appTheme === 'dark' ? 'rgb(55 65 81 / var(--tw-bg-opacity))' : 'rgb(249 250 251 / var(--tw-bg-opacity))')
                  }}
              >
              {textOutput}
              </SyntaxHighlighter>
            </div>
          </div>
          <div className="px-4 py-2 text-gray-400 border-dashed rounded dark:border-gray-600 text-right">
          <button
              onClick={() => handleCopyClick()}
              type="button"
              className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-[#2557D6]/50 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center dark:focus:ring-[#2557D6]/50 me-2 mb-2"
            >
              <FontAwesomeIcon icon={faCopy}/>
              &nbsp; Copy
            </button>
            <button
              type="button"
              onClick={() => downloadAsImage()}
              className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-[#2557D6]/50 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center dark:focus:ring-[#2557D6]/50 me-2 mb-2"
            >
              <FontAwesomeIcon icon={faCloudArrowDown}/>
              &nbsp; Download
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AsciiArtTextGenerator;
